/**
 * GemGrace wishlist — localStorage + drawer UI, variant-aware, cart integration.
 */
(function () {
  const STORAGE_KEY = 'wishlist_v1';

  function getDrawer() {
    return document.querySelector('#wishlist-drawer');
  }

  function loadWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }


  function resizeShopifyImage(src, size, crop) {
    if (!src) return '';
    let c = crop || '';
    return src
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
      .replace(/\.(jpg|png|gif|jpeg|webp)/gi, (match) => {
        if (c.length) c = `_crop_${c}`;
        return `_${size}${c}${match}`;
      });
  }

  function formatMoney(cents, formatString) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = formatString || getDrawer()?.dataset.moneyFormat || '{{amount}}';

    function def(opt, d) {
      return typeof opt === 'undefined' ? d : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = def(precision, 2);
      thousands = def(thousands, ',');
      decimal = def(decimal, '.');
      if (Number.isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const dec = parts[1] ? decimal + parts[1] : '';
      return dollars + dec;
    }

    const m = fmt.match(placeholderRegex);
    if (!m) return String(cents);
    let value = '';
    switch (m[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function pickVariant(product) {
    if (!product?.variants?.length) return null;
    const fromForm = document.querySelector('.product-form input[name="id"]');
    if (fromForm) {
      const vid = parseInt(fromForm.value, 10);
      const v = product.variants.find((x) => x.id === vid);
      if (v) return v;
    }
    return product.variants.find((x) => x.available) || product.variants[0];
  }

  async function enrichProductEntry(entry) {
    if (entry.variant_id != null && typeof entry.available === 'boolean') return entry;
    try {
      const res = await fetch(`/products/${encodeURIComponent(entry.handle)}.js`);
      if (!res.ok) return entry;
      const product = await res.json();
      let v =
        entry.variant_id != null
          ? product.variants.find((x) => x.id === Number(entry.variant_id))
          : null;
      if (!v) {
        v = product.variants.find((x) => x.available) || product.variants[0];
      }
      return {
        ...entry,
        variant_id: v?.id ?? entry.variant_id,
        available: v ? v.available !== false : false,
        price: product.price ?? entry.price,
        compare_at_price: product.compare_at_price ?? entry.compare_at_price,
        featured_image: product.featured_image || entry.featured_image,
        title: product.title || entry.title,
        url: product.url || entry.url,
      };
    } catch {
      return entry;
    }
  }

  async function enrichWishlist(list) {
    const next = await Promise.all(
      list.map((item) =>
        item.variant_id != null && typeof item.available === 'boolean'
          ? Promise.resolve(item)
          : enrichProductEntry(item)
      )
    );
    saveWishlist(next);
    return next;
  }

  function syncWishlistButtons() {
    const wishlist = loadWishlist();
    document.querySelectorAll('.button-add-remove-wishlist').forEach((btn) => {
      const handle = btn.closest('[data-product-handle]')?.dataset.productHandle;
      if (!handle) return;

      const isListed = wishlist.some((e) => e.handle === handle);
      const addEl = btn.querySelector('[data-add]');
      const removeEl = btn.querySelector('[data-remove]');
      if (addEl) addEl.style.display = isListed ? 'none' : 'inline-flex';
      if (removeEl) removeEl.style.display = isListed ? 'inline-flex' : 'none';
      btn.style.display = btn.classList.contains('button--full-width') ? 'block' : 'flex';
    });

    document.querySelectorAll('.wishlist-count-bubble').forEach((bubble) => {
      if (wishlist.length === 0) {
        bubble.style.display = 'none';
      } else {
        bubble.style.display = 'flex';
        bubble.textContent = String(wishlist.length);
      }
    });

    const drawer = getDrawer();
    const ariaAdd = drawer?.dataset.textAdd || 'Add to wishlist';
    const ariaRemove = drawer?.dataset.textRemove || 'Remove from wishlist';

    document.querySelectorAll('.card-action-btn-wishlist').forEach((btn) => {
      const handle = btn.closest('[data-product-handle]')?.dataset.productHandle;
      if (!handle) return;
      const isListed = wishlist.some((e) => e.handle === handle);
      btn.innerHTML = isListed ? SVG_HEART_FILLED : SVG_HEART_OUTLINE;
      btn.setAttribute('data-wishlist-active', isListed ? 'true' : 'false');
      btn.setAttribute('aria-label', isListed ? ariaRemove : ariaAdd);
    });
  }

  const SVG_HEART_FILLED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 9.1371C2 14 6.01943 16.5914 8.96173 18.9109C10 19.7294 11 20.5 12 20.5C13 20.5 14 19.7294 15.0383 18.9109C17.9806 16.5914 22 14 22 9.1371C22 4.27416 16.4998 0.825464 12 5.50063C7.50016 0.825464 2 4.27416 2 9.1371Z" fill="currentColor"></path></svg>';
  const SVG_HEART_OUTLINE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.96173 18.9109L9.42605 18.3219L8.96173 18.9109ZM12 5.50063L11.4596 6.02073C11.601 6.16763 11.7961 6.25063 12 6.25063C12.2039 6.25063 12.399 6.16763 12.5404 6.02073L12 5.50063ZM15.0383 18.9109L15.5026 19.4999L15.0383 18.9109ZM9.42605 18.3219C7.91039 17.1271 6.25307 15.9603 4.93829 14.4798C3.64922 13.0282 2.75 11.3345 2.75 9.1371H1.25C1.25 11.8026 2.3605 13.8361 3.81672 15.4758C5.24723 17.0866 7.07077 18.3752 8.49742 19.4999L9.42605 18.3219ZM2.75 9.1371C2.75 6.98623 3.96537 5.18252 5.62436 4.42419C7.23607 3.68748 9.40166 3.88258 11.4596 6.02073L12.5404 4.98053C10.0985 2.44352 7.26409 2.02539 5.00076 3.05996C2.78471 4.07292 1.25 6.42503 1.25 9.1371H2.75ZM8.49742 19.4999C9.00965 19.9037 9.55954 20.3343 10.1168 20.6599C10.6739 20.9854 11.3096 21.25 12 21.25V19.75C11.6904 19.75 11.3261 19.6293 10.8736 19.3648C10.4213 19.1005 9.95208 18.7366 9.42605 18.3219L8.49742 19.4999ZM15.5026 19.4999C16.9292 18.3752 18.7528 17.0866 20.1833 15.4758C21.6395 13.8361 22.75 11.8026 22.75 9.1371H21.25C21.25 11.3345 20.3508 13.0282 19.0617 14.4798C17.7469 15.9603 16.0896 17.1271 14.574 18.3219L15.5026 19.4999ZM22.75 9.1371C22.75 6.42503 21.2153 4.07292 18.9992 3.05996C16.7359 2.02539 13.9015 2.44352 11.4596 4.98053L12.5404 6.02073C14.5983 3.88258 16.7639 3.68748 18.3756 4.42419C20.0346 5.18252 21.25 6.98623 21.25 9.1371H22.75ZM14.574 18.3219C14.0479 18.7366 13.5787 19.1005 13.1264 19.3648C12.6739 19.6293 12.3096 19.75 12 19.75V21.25C12.6904 21.25 13.3261 20.9854 13.8832 20.6599C14.4405 20.3343 14.9903 19.9037 15.5026 19.4999L14.574 18.3219Z" fill="currentColor"></path></svg>';

  window.onClickWishlistButton = async function (btn, event) {
    if (event) event.preventDefault();
    const drawer = getDrawer();
    if (!drawer) return;

    const productHandle = btn.closest('[data-product-handle]')?.dataset.productHandle;
    if (!productHandle) return;

    let list = loadWishlist();
    const exists = list.some((e) => e.handle === productHandle);

    if (exists) {
      list = list.filter((e) => e.handle !== productHandle);
    } else {
      try {
        const res = await fetch(`/products/${encodeURIComponent(productHandle)}.js`);
        const product = await res.json();
        const v = pickVariant(product);
        list.push({
          id: product.id,
          handle: productHandle,
          url: product.url,
          title: product.title,
          compare_at_price: product.compare_at_price,
          compare_at_price_varies: product.compare_at_price_varies,
          price: product.price,
          featured_image: product.featured_image,
          vendor: product.vendor,
          variant_id: v?.id,
          available: v ? v.available !== false : false,
          time: Date.now(),
        });
      } catch (e) {
        console.error(e);
        return;
      }
    }

    saveWishlist(list);
    syncWishlistButtons();
    buildWishlistDrawer();
  };

  function closeWishlistDrawer() {
    const el = document.querySelector('#wishlist-drawer');
    if (!el || typeof bootstrap === 'undefined') return;
    let inst = bootstrap.Offcanvas.getInstance(el);
    if (!inst) inst = bootstrap.Offcanvas.getOrCreateInstance(el);
    inst.hide();
  }

  async function wishlistAddToCart(variantId, button) {
    const routes = window.routes;
    if (!routes?.cart_add_url || !variantId) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    if (cartDrawer) {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    } else if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartNotification.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    }

    const config = {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: formData,
    };

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    try {
      const res = await fetch(routes.cart_add_url, config);
      const data = await res.json();
      if (data.status) {
        const msg = data.description || data.message || 'Cart error';
        if (window.cartStrings?.error) {
          alert(msg);
        } else {
          alert(msg);
        }
        return;
      }
      closeWishlistDrawer();
      if (cartDrawer && data.sections) {
        cartDrawer.renderContents(data);
      } else if (cartNotification && data.sections) {
        cartNotification.renderContents(data);
      } else {
        window.location.href = routes.cart_url;
      }
    } catch (e) {
      console.error(e);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }

  let listDelegateBound = false;

  function bindWishlistDrawerListActions() {
    const list = document.querySelector('#wishlist-drawer-product-list');
    if (!list || listDelegateBound) return;
    listDelegateBound = true;

    list.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('.btn-remove-from-wishlist');
      if (removeBtn) {
        e.preventDefault();
        const handle = removeBtn.dataset.productHandle;
        let wl = loadWishlist().filter((x) => x.handle !== handle);
        saveWishlist(wl);
        syncWishlistButtons();
        buildWishlistDrawer();
        return;
      }

      const addBtn = e.target.closest('.wishlist-drawer__add-to-cart');
      if (addBtn && !addBtn.disabled) {
        e.preventDefault();
        const vid = parseInt(addBtn.dataset.variantId, 10);
        await wishlistAddToCart(vid, addBtn);
      }
    });
  }

  async function buildWishlistDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;

    const emptyEl = drawer.querySelector('#wishlist-drawer-empty');
    const listEl = drawer.querySelector('#wishlist-drawer-product-list');
    if (!emptyEl || !listEl) return;

    let wishlist = loadWishlist();
    const needsEnrich =
      wishlist.length > 0 &&
      wishlist.some((p) => p.variant_id == null || typeof p.available !== 'boolean');
    if (needsEnrich) {
      wishlist = await enrichWishlist(wishlist);
    }

    const imgSize = drawer.dataset.imgSize || '120x120';
    const imgW = drawer.dataset.imgWidth || '120';
    const imgH = drawer.dataset.imgHeight || '120';
    const moneyFmt = drawer.dataset.moneyFormat;
    const textRemove = drawer.dataset.textRemove || 'Remove';
    const textAddCart = drawer.dataset.textAddToCart || 'Add to cart';
    const textSoldOut = drawer.dataset.textSoldOut || 'Sold out';

    listEl.innerHTML = '';

    if (wishlist.length === 0) {
      emptyEl.hidden = false;
      listEl.hidden = true;
      return;
    }

    emptyEl.hidden = true;
    listEl.hidden = false;

    const frag = document.createDocumentFragment();
    wishlist.forEach((product) => {
      const li = document.createElement('li');
      li.className = 'wishlist-drawer__item';
      li.dataset.productHandle = product.handle;

      const imgSrc = product.featured_image
        ? resizeShopifyImage(product.featured_image, imgSize)
        : '';
      const compareShown =
        product.compare_at_price != null &&
        Number(product.compare_at_price) > Number(product.price || 0);
      const variantId = product.variant_id;
      const canAdd = variantId && product.available;

      li.innerHTML = `
        <div class="wishlist-drawer__row">
          <a href="${product.url}" class="wishlist-drawer__media">${imgSrc ? `<img src="${imgSrc}" alt="" width="${imgW}" height="${imgH}" loading="lazy">` : ''}</a>
          <div class="wishlist-drawer__main">
            <div class="wishlist-drawer__headline">
              <a href="${product.url}" class="wishlist-drawer__title break">${product.title || ''}</a>
              <button
                type="button"
                class="wishlist-drawer__remove btn-remove-from-wishlist button button--tertiary"
                aria-label="${textRemove}"
                data-product-handle="${product.handle}"
              >
                <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/><path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"/></svg>
              </button>
            </div>
            <div class="wishlist-drawer__price">
              ${compareShown ? `<s class="wishlist-drawer__price-compare"><span class="visually-hidden">Previous price</span>${formatMoney(product.compare_at_price, moneyFmt)}</s>` : ''}
              <span class="wishlist-drawer__price-final">${formatMoney(product.price, moneyFmt)}</span>
            </div>
            <button
              type="button"
              class="wishlist-drawer__add-to-cart button button--primary"
              data-variant-id="${variantId || ''}"
              ${canAdd ? '' : 'disabled'}
              aria-label="${canAdd ? textAddCart : textSoldOut}"
            >
              <span>${canAdd ? textAddCart : textSoldOut}</span>
            </button>
          </div>
        </div>
      `;
      frag.appendChild(li);
    });

    listEl.appendChild(frag);
    bindWishlistDrawerListActions();
  }

  function insertHeaderIcon() {
    const drawer = getDrawer();
    if (!drawer) return;

    const html = `
      <a
        id="wishlist-icon-bubble"
        class="header__icon header__icon--wishlist link focus-inset"
        href="#wishlist-drawer"
        role="button"
        data-bs-toggle="offcanvas"
        aria-controls="wishlist-drawer">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M8.96173 18.9109L9.42605 18.3219L8.96173 18.9109ZM12 5.50063L11.4596 6.02073C11.601 6.16763 11.7961 6.25063 12 6.25063C12.2039 6.25063 12.399 6.16763 12.5404 6.02073L12 5.50063ZM15.0383 18.9109L15.5026 19.4999L15.0383 18.9109ZM9.42605 18.3219C7.91039 17.1271 6.25307 15.9603 4.93829 14.4798C3.64922 13.0282 2.75 11.3345 2.75 9.1371H1.25C1.25 11.8026 2.3605 13.8361 3.81672 15.4758C5.24723 17.0866 7.07077 18.3752 8.49742 19.4999L9.42605 18.3219ZM2.75 9.1371C2.75 6.98623 3.96537 5.18252 5.62436 4.42419C7.23607 3.68748 9.40166 3.88258 11.4596 6.02073L12.5404 4.98053C10.0985 2.44352 7.26409 2.02539 5.00076 3.05996C2.78471 4.07292 1.25 6.42503 1.25 9.1371H2.75ZM8.49742 19.4999C9.00965 19.9037 9.55954 20.3343 10.1168 20.6599C10.6739 20.9854 11.3096 21.25 12 21.25V19.75C11.6904 19.75 11.3261 19.6293 10.8736 19.3648C10.4213 19.1005 9.95208 18.7366 9.42605 18.3219L8.49742 19.4999ZM15.5026 19.4999C16.9292 18.3752 18.7528 17.0866 20.1833 15.4758C21.6395 13.8361 22.75 11.8026 22.75 9.1371H21.25C21.25 11.3345 20.3508 13.0282 19.0617 14.4798C17.7469 15.9603 16.0896 17.1271 14.574 18.3219L15.5026 19.4999ZM22.75 9.1371C22.75 6.42503 21.2153 4.07292 18.9992 3.05996C16.7359 2.02539 13.9015 2.44352 11.4596 4.98053L12.5404 6.02073C14.5983 3.88258 16.7639 3.68748 18.3756 4.42419C20.0346 5.18252 21.25 6.98623 21.25 9.1371H22.75ZM14.574 18.3219C14.0479 18.7366 13.5787 19.1005 13.1264 19.3648C12.6739 19.6293 12.3096 19.75 12 19.75V21.25C12.6904 21.25 13.3261 20.9854 13.8832 20.6599C14.4405 20.3343 14.9903 19.9037 15.5026 19.4999L14.574 18.3219Z" fill="currentColor"></path>
        </svg>
        <span class="visually-hidden">${drawer.dataset.textWishlist || 'Wishlist'}</span>
        <div class="wishlist-count-bubble" style="display: none;" aria-hidden="true"></div>
      </a>
    `;

    const cartIcon = document.querySelector('#cart-icon-bubble');
    const menuDrawer = document.querySelector('#Details-menu-drawer-container');

    if (cartIcon) {
      cartIcon.insertAdjacentHTML('beforebegin', html);
    } else if (menuDrawer) {
      menuDrawer.insertAdjacentHTML('afterend', html);
    }
  }

  function insertPdpButton() {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.displayBtnPdp === 'false') return;

    const html = `
      <button
        class="button-add-remove-wishlist button button--full-width button--tertiary"
        type="button"
        onclick="onClickWishlistButton(this, event)"
        style="display: none;">
        <span data-add>
          ${SVG_HEART_OUTLINE}
          ${drawer.dataset.textAdd || ''}
        </span>
        <span data-remove style="display: none;">
          ${SVG_HEART_FILLED}
          ${drawer.dataset.textRemove || ''}
        </span>
      </button>
    `;
    const productForm = document.querySelector('.product .product-form');
    if (productForm) productForm.insertAdjacentHTML('afterend', html);
  }

  function openDrawerFromHash() {
    if (!window.location.hash.includes('wishlist')) return;
    const el = getDrawer();
    if (!el || typeof bootstrap === 'undefined') return;
    setTimeout(() => {
      bootstrap.Offcanvas.getOrCreateInstance(el).show();
    }, 200);
  }

  function init() {
    insertHeaderIcon();
    insertPdpButton();
    syncWishlistButtons();
    bindWishlistDrawerListActions();
    buildWishlistDrawer();

    if (document.readyState === 'complete') {
      openDrawerFromHash();
    } else {
      window.addEventListener('load', openDrawerFromHash);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
